import { isCustomPage } from "../../utils/path/library";

import { DEFAULT_TRACKING } from "./constants";
import { PAGE_TYPES } from "../../constants/tracking";
import { isBrowser } from "../../utils/environmentDetection/library";
import { ESCAPED_CHARS } from "../../constants/html";

/**
 * Utility function to change format of attribution
 *
 * @param {string} attributionString - attributionLevels string in form level1/level2/level3, e.g. "programming/unscripted/docs"
 * @returns {object} attributionObject - attributionLevels reformatted as object in form e.g. { level1: 'programming', level2: 'unscripted', level3: 'docs' }
 */
export const attributionStringToObject = (attributionString) => {
    if (!attributionString) {
        return {};
    }

    const attributionArray = attributionString.split("/");

    const attributionObject = attributionArray.reduce(
        (attribution, value, index) => {
            if (value) {
                attribution[`level${index + 1}`] = value;
            }
            return attribution;
        },
        {}
    );
    return attributionObject;
};

/**
 * Check if stats event tracker initialized
 * @return {boolean} true if initialized
 */
export const isEventsEnabled = () =>
    isBrowser() && !!window.CBC?.APP?.SC?.EventTracker;

/**
 * This helper function populates a json object with tracking information, based on different pages
 * @param {object} app - the app state
 * @param {object} json - the json object from API
 * @returns {object} the updated tracking json data
 */
export function populateTrackingObject(app, json = {}) {
    if (!app) {
        return {};
    }

    let trackingObject = {
        pageType: app.pageType,
    };

    const contentArea = app.baseSection ? app.baseSection : "misc";

    trackingObject = Object.assign(json, trackingObject, {
        url: app.meta.canonical,
        tracking: {
            contentArea: contentArea,
            contenttype: "index",
            ...json.tracking, // This is for merging existing tracking information that comes from a pages Endpoint call, while staying backwards compatible.
        },
    });

    // Otherwise, it's probably a custom page that doesn't get content from any APIs
    // Get the values from our hardcoded constants
    if (app.pageType === PAGE_TYPES.CUSTOM) {
        const customTracking = isCustomPage(app);
        if (customTracking && customTracking.metadata) {
            trackingObject.tracking = customTracking.metadata.tracking;
        }
    }

    return trackingObject;
}

export const trackSearchedEvent = (content, custom, feature) => {
    if (isEventsEnabled()) {
        window.CBC.APP.SC.EventTracker.trackEvent("SEARCHED", {
            content,
            app: {
                name: "ocelot",
            },
            custom,
            feature,
        });
    }
};

export const trackToggleButtonClick = (
    toggleName,
    toggleOpen,
    togglePlacement,
    toggleInstance
) => {
    // Check if the toggle is open or closed
    if (toggleOpen === false) {
        // The toggle is currently closed; This will open the toggle
        trackOpenedClosedActions(
            `OPENED`,
            toggleName,
            togglePlacement,
            toggleInstance
        );
    } else {
        // The toggle is currenlt open; This will close the toggle
        trackOpenedClosedActions(
            `CLOSED`,
            toggleName,
            togglePlacement,
            toggleInstance
        );
    }
};

const trackOpenedClosedActions = (
    eventType,
    featureName,
    position,
    instance
) => {
    // Verify the EventTracker is on
    turnEventTrackerOn();

    if (isEventsEnabled()) {
        window.CBC.APP.SC.EventTracker.trackEvent(eventType, {
            feature: {
                name: featureName,
                position: position,
                instance: instance,
            },
            app: {
                name: "ocelot",
            },
        });
    }
};

export const trackSelectedButtonClick = (
    toggleName,
    toggleInstance,
    togglePlacement,
    toggleValue
) => {
    trackSelectedActions(
        `SELECTED`,
        toggleName,
        toggleInstance,
        togglePlacement,
        toggleValue
    );
};

const trackSelectedActions = (
    eventType,
    featureName,
    instance,
    position,
    toggleValue
) => {
    // Verify the EventTracker is on
    turnEventTrackerOn();

    if (isEventsEnabled()) {
        window.CBC.APP.SC.EventTracker.trackEvent(eventType, {
            feature: {
                name: featureName,
                instance: instance,
                position: position,
                value: toggleValue,
            },
            app: {
                name: "ocelot",
            },
        });
    }
};

export const turnEventTrackerOn = () => {
    if (isEventsEnabled()) {
        /* Information needed to get passed */
        if (window.CBC.APP.SC.EventTracker.isOn() === false) {
            window.CBC.APP.SC.EventTracker.turnOn();
        }
    }
};

export const buildTracking = ({
    id,
    title,
    description,
    tracking,
    attribution,
    pageType,
}) => {
    if (!tracking && !attribution && !id) {
        return DEFAULT_TRACKING;
    }

    let unescapedTitle = title || "";

    ESCAPED_CHARS.forEach((char) => {
        const [k, v] = char;
        const regex = new RegExp(k, "gi");

        unescapedTitle = unescapedTitle.replace(regex, v);
    });

    if (tracking || attribution) {
        return {
            type: pageType || "",
            title: unescapedTitle,
            description: description || "",
            id: id || "",
            trackingId: id || "",
            contentarea: tracking.contentArea || "",
            contenttype: tracking.contenttype || "",
            subsection1: tracking.subSection1 || "",
            subsection2: tracking.subSection2 || "",
            subsection3: tracking.subSection3 || "",
            subsection4: tracking.subSection4 || "",
            //we are converting attribution which now comes from graphQL as a string back into
            //the old object format as required by Amplitude and Stats.js
            department: attributionStringToObject(attribution) || {},
        };
    } else {
        return {
            ...DEFAULT_TRACKING,
            type: "landing",
        };
    }
};
